import axios, { type AxiosResponse } from "axios";
import {
  awsJWTAuthAxiosInterceptor,
  awsJWTAuthErrorAxiosInterceptor,
} from "src/api/AWSAxiosInterceptor";
import type {
  BulkOperationsRequest,
  BulkOperationsResponse,
} from "src/api/interfaces/bulk_operations";
import type {
  GetComplianceUserActionsRequest,
  GetMetadataUserActionsRequest,
  GetUserActionsMetadataResponse,
  GetUserActionsResponse,
  GetValidationUserActionsRequest,
} from "src/api/interfaces/compliance_user_actions";
import type {
  GetDocumentsRequest,
  GetDocumentsResult,
} from "src/api/interfaces/document_listing";
import type {
  GetDocumentComplianceRequest,
  GetDocumentComplianceResponse,
} from "src/api/interfaces/document_validation";
import type {
  GetDownloadDocumentRequest,
  GetDownloadDocumentResponse,
} from "src/api/interfaces/download_document";
import type {
  GetFilterOptionsRequest,
  GetFilterOptionsResponse,
} from "src/api/interfaces/filter_options";
import type {
  GetHistoryRequest,
  GetHistoryResponse,
} from "src/api/interfaces/history";
import type {
  GetInboundComplianceGroupDetailsRequest,
  GetInboundComplianceGroupDetailsResponse,
  GetInboundComplianceGroupRequest,
  GetInboundComplianceGroupResponse,
} from "src/api/interfaces/inbound_compliance_group";
import {
  convertToInboundDocument,
  type GetInboundDocumentRequest,
  type GetInboundDocumentResponse,
  type RevalidateDocumentRequest,
  type RevalidateDocumentResponse,
  type SaveOverriddenMetadataRequest,
  type SaveOverriddenMetadataResponse,
} from "src/api/interfaces/inbound_document";
import type {
  GetDocumentLineItemsRequest,
  GetDocumentLineItemsResponse,
} from "src/api/interfaces/line_item";
import type { GetPermissionsResult } from "src/api/interfaces/permissions";
import {
  convertToRelatedDocuments,
  convertToRelatesToDocuments,
  type GetRelatedDocumentsByTypeResponse,
  type GetRelatedDocumentsRequest,
  type GetRelatedDocumentsResponse,
} from "src/api/interfaces/related_documents";
import type {
  GetValidationResultRequest,
  GetValidationResultResponse,
} from "src/api/interfaces/validation_result";

import {
  isValidDateFormat,
  transformKeysCamelCaseToSnakeCase,
} from "src/utils";
import {
  clearPayeeIntegrationCookie,
  getPayeeIntegrationCookie,
} from "src/cookies";
import { LogLevel, log } from "../logger";
import { getApiBaseUrl, isPayeeCentral, isStandalone } from "./config";
import { customValueEncode } from "./encoder";
import type { NestUseCase } from "./interfaces/nest";
import type {
  NestGetUseCasesParams,
  NestGetHistoryParams,
  NestGetHistoryResponse,
  NestSubmitPayload,
  NestGetPartnersParams,
  NestGetPartnersResponse,
  NestGetSessionInfoResponse,
  NestGetPresignedURLParams,
  NestGetPresignedURLResponse,
} from "./interfaces/nest/api";
import type { NestTestResult } from "./interfaces/nest/history";

axios.defaults.baseURL = getApiBaseUrl();
const DEFAULT_RESPONSE_INTERCEPTOR = (response: AxiosResponse): AxiosResponse =>
  response;

if (isPayeeCentral()) {
  axios.interceptors.request.use((requestConfig) => {
    const payeeToken = getPayeeIntegrationCookie();
    if (payeeToken) {
      requestConfig.headers.set("Authorization", payeeToken);
    }
    return requestConfig;
  });
} else if (isStandalone()) {
  axios.interceptors.request.use(awsJWTAuthAxiosInterceptor);
  axios.interceptors.response.use(
    DEFAULT_RESPONSE_INTERCEPTOR,
    awsJWTAuthErrorAxiosInterceptor
  );
}

export const UNAUTHORIZED_HTTP_STATUS_CODE = 401;

const commonPath = "/inbound-central/api";

const PAYEE_CENTRAL_URL = "https://payeecentral.amazon.com/";

const handleNestPayeeError = (error: any): never => {
  if (
    isPayeeCentral() &&
    error?.response?.status === UNAUTHORIZED_HTTP_STATUS_CODE
  ) {
    clearPayeeIntegrationCookie();
    window.location.replace(PAYEE_CENTRAL_URL);
  }
  throw error;
};

const client = {
  getPermissions: async (): Promise<GetPermissionsResult> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching permissions",
      operationNamespace: "client.getPermissions",
    });
    return axios
      .get<RequestResultData>(`${commonPath}/permissions`, {
        baseURL: getApiBaseUrl(),
      })
      .then(({ data }: AxiosResponse): GetPermissionsResult => data);
  },
  getDocuments: async ({
    page,
    page_size,
    countryCode,
    filter,
  }: GetDocumentsRequest): Promise<GetDocumentsResult> => {
    const params = {
      page,
      page_size,
      country_code: countryCode,
      search: filter?.search,
      issuance_date: filter?.issuanceDate,
      start_issuance_date: filter?.startIssuanceDate?.slice(0, 10),
      end_issuance_date: filter?.endIssuanceDate?.slice(0, 10),
      use_cases: (filter?.useCase as string[])?.join(","),
      document_subtype: filter?.documentSubtype,
      document_type: filter?.documentType,
      statuses: (filter?.statuses as string[])?.join(","),
      validation_groups: (filter?.validationGroups as string[])?.join(","),
      orchestration_events: (filter?.orchestrationEvents as string[])?.join(
        ","
      ),
      document_ids: (filter?.documentId as string[])?.join(","),
      partner_codes: (filter?.partnerCode as string[])?.join(","),
      referenced_documents: (filter?.referencedDocument as string[])?.join(","),
      referenced_documents_is_empty: filter?.referencedDocumentsIsEmpty,
    };

    if (
      !isValidDateFormat(params.start_issuance_date as string) ||
      !isValidDateFormat(params.end_issuance_date as string)
    ) {
      console.error("Invalid date format on search filter");
      throw Error("Invalid date format");
    }

    log({
      level: LogLevel.INFO,
      message: "Fetching documents",
      operationNamespace: "client.getDocuments",
      attributes: { params },
    });
    return axios
      .get<RequestResultData>(`${commonPath}/documents`, {
        baseURL: getApiBaseUrl(),
        params,
      })
      .then(({ data }: AxiosResponse): GetDocumentsResult => data);
  },
  getFilterOptions: async ({
    useCases,
    countryCode,
  }: GetFilterOptionsRequest): Promise<GetFilterOptionsResponse> => {
    const params = {
      use_cases: useCases?.join(","),
    };
    return axios
      .get<RequestResultData>(`${commonPath}/filter-options/${countryCode}`, {
        baseURL: getApiBaseUrl(),
        params,
      })
      .then(({ data }: AxiosResponse): GetFilterOptionsResponse => data);
  },
  getComplianceUserActions: async (
    request: GetComplianceUserActionsRequest
  ): Promise<GetUserActionsResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching compliance user actions",
      operationNamespace: "client.getComplianceUserActions",
      attributes: { request },
    });
    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${request.country_code}/document-types/${request.document_type}` +
          `/documents/${customValueEncode(
            request.document_id
          )}/compliance/user-actions`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetUserActionsResponse => ({
        userActions: data,
      }));
  },
  getValidationUserActions: async (
    request: GetValidationUserActionsRequest
  ): Promise<GetUserActionsResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching validation user actions",
      operationNamespace: "client.getValidationUserActions",
      attributes: { request },
    });
    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${request.country_code}/document-types/${request.document_type}` +
          `/documents/${customValueEncode(
            request.document_id
          )}/validation/user-actions`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetUserActionsResponse => data);
  },
  getMetadataUserActions: async (
    request: GetMetadataUserActionsRequest
  ): Promise<{ [key: string]: any }> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching metadata user actions",
      operationNamespace: "client.getMetadataUserActions",
      attributes: { request },
    });
    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${request.countryCode}/document-types/${request.documentType}` +
          `/document-subtypes/${request.documentSubtype}/use-cases/${request.useCase}/user-action/metadata`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetUserActionsMetadataResponse => data);
  },
  getInboundDocument: async (
    request: GetInboundDocumentRequest
  ): Promise<GetInboundDocumentResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching document details request",
      operationNamespace: "client.getInboundDocument",
      attributes: { request },
    });
    const { countryCode, documentType, documentId } = request;

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetInboundDocumentResponse => {
        return { inboundDocument: convertToInboundDocument(data) };
      });
  },
  getHistory: async (
    request: GetHistoryRequest
  ): Promise<GetHistoryResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching history request",
      operationNamespace: "client.getHistory",
      attributes: { request },
    });
    const { countryCode, documentType, documentId } = request;

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/history`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetHistoryResponse => data);
  },
  getDocumentLineItems: async (
    request: GetDocumentLineItemsRequest
  ): Promise<GetDocumentLineItemsResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching document line items request",
      operationNamespace: "client.getDocumentLineItems",
      attributes: { request },
    });
    const { countryCode, documentType, documentId } = request;

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/line-items`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetDocumentLineItemsResponse => data);
  },
  getRelatedDocuments: async (
    request: GetRelatedDocumentsRequest
  ): Promise<GetRelatedDocumentsResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching related documents from a document",
      operationNamespace: "client.getRelatedDocuments",
      attributes: { request },
    });
    const { countryCode, documentType, documentId } = request;

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/related-documents`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetRelatedDocumentsResponse => ({
        relatedDocuments: convertToRelatedDocuments(data),
        relatesToDocuments: convertToRelatesToDocuments(data),
      }));
  },
  getRelatedDocumentsByType: async (
    request: GetRelatedDocumentsRequest
  ): Promise<GetRelatedDocumentsByTypeResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching related documents by type from a document",
      operationNamespace: "client.getRelatedDocumentsByType",
      attributes: { request },
    });
    const { countryCode, documentType, documentId, relatedDocumentType } =
      request;

    return axios
      .get<any>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/related-documents/${relatedDocumentType}`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(
        ({ data }: AxiosResponse): GetRelatedDocumentsByTypeResponse => data
      );
  },
  getInboundComplianceGroups: async (
    request: GetInboundComplianceGroupRequest
  ): Promise<GetInboundComplianceGroupResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching inbound compliance group",
      operationNamespace: "client.getInboundComplianceGroups",
      attributes: { request },
    });
    const { countryCode, documentType, documentId, executionNumber } = request;
    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/executions/${executionNumber}/inbound-compliance`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(
        ({ data }: AxiosResponse): GetInboundComplianceGroupResponse => data
      );
  },
  getInboundComplianceGroupDetails: async (
    request: GetInboundComplianceGroupDetailsRequest
  ): Promise<GetInboundComplianceGroupDetailsResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Requesting inbound compliance group details",
      operationNamespace: "client.getInboundComplianceGroupDetails",
      attributes: { request },
    });
    const {
      countryCode,
      documentType,
      documentId,
      eventSubtype,
      executionNumber,
    } = request;

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/executions/${executionNumber}/inbound-compliance/${eventSubtype}`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(
        ({
          data: { inboundComplianceGroupDetails },
        }: AxiosResponse): GetInboundComplianceGroupDetailsResponse => ({
          inboundComplianceGroupDetails,
        })
      );
  },
  getDocumentCompliance: async (
    request: GetDocumentComplianceRequest
  ): Promise<GetDocumentComplianceResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Fetching document validation",
      operationNamespace: "client.getDocumentValidation",
      attributes: { request },
    });
    const { countryCode, documentType, documentId, executionNumber } = request;

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/executions/${executionNumber}/validations`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): GetDocumentComplianceResponse => data);
  },
  revalidateDocument: async (
    request: RevalidateDocumentRequest
  ): Promise<RevalidateDocumentResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Starting Document Revalidation",
      operationNamespace: "client.revalidateDocument",
      attributes: { request },
    });
    const { countryCode, documentType, documentId } = request;

    return axios
      .post<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/revalidate`,
        undefined,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): RevalidateDocumentResponse => data);
  },
  saveOverriddenMetadata: async (
    request: SaveOverriddenMetadataRequest
  ): Promise<SaveOverriddenMetadataResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Starting Saving Overridden Metadata",
      operationNamespace: "client.saveOverriddenMetadata",
      attributes: { request },
    });
    const { countryCode, documentType, documentId } = request;

    return axios
      .post<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/save-overridden-metadata`,
        request,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): SaveOverriddenMetadataResponse => data);
  },
  getDownloadDocumentUrl: async (
    request: GetDownloadDocumentRequest
  ): Promise<GetDownloadDocumentResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Requesting document from Aduana",
      operationNamespace: "client.getDownloadDocumentUrl",
      attributes: { request },
    });
    const { countryCode, documentType, documentId, attachment } = request;
    const queryParams: { [key: string]: string | undefined } = {};

    if (attachment) {
      queryParams.attachment = attachment;
    }

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/download`,
        {
          baseURL: getApiBaseUrl(),
          params: queryParams,
        }
      )
      .then(({ data }: AxiosResponse): GetDownloadDocumentResponse => data);
  },
  getValidationResult: async (
    request: GetValidationResultRequest
  ): Promise<GetValidationResultResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Requesting validation result",
      operationNamespace: "client.getValidationResult",
      attributes: { request },
    });
    const {
      countryCode,
      documentType,
      documentId,
      validationGroup,
      executionNumber,
    } = request;

    return axios
      .get<RequestResultData>(
        `${commonPath}/countries/${countryCode}/document-types/${documentType}/documents/${customValueEncode(
          documentId
        )}/executions/${executionNumber}/validations/${validationGroup}`,
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(
        ({
          data: { validationResults, exceedsSizeLimits },
        }: AxiosResponse): GetValidationResultResponse => ({
          validationResults,
          exceedsSizeLimits,
        })
      );
  },
  performBulkOperations: async (
    request: BulkOperationsRequest
  ): Promise<BulkOperationsResponse> => {
    log({
      level: LogLevel.INFO,
      message: "Starting Perform Bulk Operations",
      operationNamespace: "client.performBulkOperations",
      attributes: { request },
    });

    return axios
      .post<RequestResultData>(
        `${commonPath}/user-actions/perform-bulk-operation`,
        transformKeysCamelCaseToSnakeCase(request),
        {
          baseURL: getApiBaseUrl(),
        }
      )
      .then(({ data }: AxiosResponse): BulkOperationsResponse => data);
  },
  async getNestUseCases(params?: NestGetUseCasesParams) {
    return axios
      .get(`${commonPath}/nest/use-cases`, {
        baseURL: getApiBaseUrl(),
        params,
      })
      .then(({ data }): NestUseCase[] => data.useCases)
      .catch(handleNestPayeeError);
  },

  async getNestHistory(
    params?: NestGetHistoryParams,
    signal?: AbortSignal
  ): Promise<NestGetHistoryResponse> {
    return axios
      .get(`${commonPath}/nest/history`, {
        baseURL: getApiBaseUrl(),
        params,
        signal,
      })
      .then(({ data }): NestGetHistoryResponse => data)
      .catch(handleNestPayeeError);
  },

  async nestSubmit(payload: NestSubmitPayload) {
    return axios
      .post(`${commonPath}/nest/submit`, payload, {
        baseURL: getApiBaseUrl(),
      })
      .then(({ data }): NestTestResult => data)
      .catch(handleNestPayeeError);
  },

  async getNestPartners(
    params: NestGetPartnersParams
  ): Promise<NestGetPartnersResponse> {
    return axios
      .get(`${commonPath}/nest/partners`, {
        baseURL: getApiBaseUrl(),
        params,
      })
      .then(({ data }): NestGetPartnersResponse => data)
      .catch(handleNestPayeeError);
  },

  async getNestSessionInfo(): Promise<NestGetSessionInfoResponse> {
    return axios
      .get(`${commonPath}/nest/session-info`, {
        baseURL: getApiBaseUrl(),
      })
      .then(({ data }): NestGetSessionInfoResponse => data)
      .catch(handleNestPayeeError);
  },

  async getInputFilePresignedURL(
    params: NestGetPresignedURLParams
  ): Promise<NestGetPresignedURLResponse> {
    return axios
      .get(`${commonPath}/nest/download`, {
        baseURL: getApiBaseUrl(),
        params,
      })
      .then(({ data }): NestGetPresignedURLResponse => data)
      .catch(handleNestPayeeError);
  },
};

export default client;
